/**
 * Browser side of the SignInService config-unlock seal. Mirrors the C# helper at
 * `SignInService/Model/WebAdminConfigUnlock.cs:ConfigUnlockCrypto.SealForCaller`:
 *
 *   ECDH(server_priv, caller_eph_pub) → 66-byte shared
 *   salt = SHA-512(server_pub_xy ‖ caller_eph_pub_xy)               (128 bytes)
 *   info = UTF-8(aadContext)
 *   key  = HKDF-SHA512(shared, salt, info, 32)                       (AES-256)
 *   ct   = AES-256-GCM(key, payload, aad=info)                       (12-byte IV, 16-byte tag)
 *   bundle = server_pub_xy(132) ‖ iv(12) ‖ ct ‖ tag(16)              (base64url for transport)
 *
 * The browser holds the matching caller side: generates an ephemeral P-521 keypair before the
 * SignInService redirect, ships the pub as `eph_pub`, stashes the priv in sessionStorage, then
 * unseals the bundle here on callback.
 *
 * Public-key wire format is the raw uncompressed coordinate pair X∥Y (132 bytes for P-521),
 * matching SignInService. WebCrypto's "raw" import/export uses the SEC1 uncompressed form
 * (0x04 prefix + 132 bytes); we strip/prepend that byte at the boundary.
 */

const AAD_COCKPIT = "isvc-callback-v1|app=cockpit";
const P521_FIELD = 66;
const P521_PUB_RAW = P521_FIELD * 2; // 132
const GCM_NONCE = 12;
const GCM_TAG = 16;

export interface CockpitTokenBundle {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  token_type: string;
  userId: number;
  scope: string;
  chain_id: string;
}

export async function generateEphKeyPair(): Promise<{ publicRaw132: Uint8Array; privJwk: JsonWebKey }> {
  const kp = await crypto.subtle.generateKey(
    { name: "ECDH", namedCurve: "P-521" },
    true,
    ["deriveBits"],
  ) as CryptoKeyPair;
  const pubRaw = new Uint8Array(await crypto.subtle.exportKey("raw", kp.publicKey));
  // Expect SEC1 uncompressed: 0x04 || X(66) || Y(66) = 133 bytes
  if (pubRaw.length !== 1 + P521_PUB_RAW || pubRaw[0] !== 0x04) {
    throw new Error(`unexpected raw P-521 pub layout: len=${pubRaw.length} prefix=${pubRaw[0]}`);
  }
  const xy = pubRaw.slice(1);
  const privJwk = await crypto.subtle.exportKey("jwk", kp.privateKey);
  return { publicRaw132: xy, privJwk };
}

export async function unsealCockpitToken(sealedB64Url: string, callerEphPrivJwk: JsonWebKey): Promise<CockpitTokenBundle> {
  const sealed = b64urlToBytes(sealedB64Url);
  if (sealed.length < P521_PUB_RAW + GCM_NONCE + GCM_TAG) {
    throw new Error(`sealed bundle too short: ${sealed.length}`);
  }
  const serverPubXY = sealed.slice(0, P521_PUB_RAW);
  const nonce = sealed.slice(P521_PUB_RAW, P521_PUB_RAW + GCM_NONCE);
  const ctAndTag = sealed.slice(P521_PUB_RAW + GCM_NONCE);

  const callerPrivKey = await crypto.subtle.importKey(
    "jwk",
    callerEphPrivJwk,
    { name: "ECDH", namedCurve: "P-521" },
    false,
    ["deriveBits"],
  );
  const callerPubXY = jwkXyToRaw132(callerEphPrivJwk);

  const serverPubKey = await crypto.subtle.importKey(
    "raw",
    bs(prepend04(serverPubXY)),
    { name: "ECDH", namedCurve: "P-521" },
    false,
    [],
  );

  // ECDH derive 528 bits (P-521 X-coordinate) — matches the server's
  // ECDiffieHellman.DeriveRawSecretAgreement output.
  const sharedBits = await crypto.subtle.deriveBits(
    { name: "ECDH", public: serverPubKey },
    callerPrivKey,
    521 % 8 === 0 ? 521 : Math.ceil(521 / 8) * 8, // 528
  );
  const shared = new Uint8Array(sharedBits);

  const saltInput = new Uint8Array(P521_PUB_RAW * 2);
  saltInput.set(serverPubXY, 0);
  saltInput.set(callerPubXY, P521_PUB_RAW);
  const salt = new Uint8Array(await crypto.subtle.digest("SHA-512", bs(saltInput)));

  const aadBytes = new TextEncoder().encode(AAD_COCKPIT);
  const hkdfKey = await crypto.subtle.importKey("raw", bs(shared), "HKDF", false, ["deriveBits"]);
  const aesKeyBits = await crypto.subtle.deriveBits(
    { name: "HKDF", hash: "SHA-512", salt: bs(salt), info: bs(aadBytes) },
    hkdfKey,
    256,
  );
  const aesKey = await crypto.subtle.importKey("raw", aesKeyBits, "AES-GCM", false, ["decrypt"]);

  const ptBuf = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: bs(nonce), additionalData: bs(aadBytes), tagLength: 128 },
    aesKey,
    bs(ctAndTag),
  );
  const json = new TextDecoder().decode(ptBuf);
  return JSON.parse(json) as CockpitTokenBundle;
}

export function bytesToB64url(b: Uint8Array): string {
  let bin = "";
  for (let i = 0; i < b.length; i++) bin += String.fromCharCode(b[i]!);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function b64urlToBytes(s: string): Uint8Array {
  const b64 = s.replace(/-/g, "+").replace(/_/g, "/") + "===".slice((s.length + 3) % 4);
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

function prepend04(xy: Uint8Array): Uint8Array {
  if (xy.length !== P521_PUB_RAW) throw new Error(`pub xy must be ${P521_PUB_RAW} bytes, got ${xy.length}`);
  const out = new Uint8Array(1 + P521_PUB_RAW);
  out[0] = 0x04;
  out.set(xy, 1);
  return out;
}

function jwkXyToRaw132(jwk: JsonWebKey): Uint8Array {
  if (!jwk.x || !jwk.y) throw new Error("jwk missing x/y");
  const x = padLeft(b64urlToBytes(jwk.x), P521_FIELD);
  const y = padLeft(b64urlToBytes(jwk.y), P521_FIELD);
  const out = new Uint8Array(P521_PUB_RAW);
  out.set(x, 0);
  out.set(y, P521_FIELD);
  return out;
}

function padLeft(b: Uint8Array, len: number): Uint8Array {
  if (b.length === len) return b;
  if (b.length > len) return b.slice(b.length - len);
  const out = new Uint8Array(len);
  out.set(b, len - b.length);
  return out;
}

/**
 * WebCrypto `BufferSource` cast. TS 5.7+ tightened `Uint8Array<ArrayBufferLike>` so it no longer
 * silently coerces to the `ArrayBufferView<ArrayBuffer>` that lib.dom requires for crypto APIs.
 * Every Uint8Array we hand to `crypto.subtle.*` here is owned by this module and never aliases
 * a SharedArrayBuffer, so the cast is sound.
 */
function bs(b: Uint8Array): BufferSource {
  return b as BufferSource;
}
